import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../firebase';
import { verifyPasswordResetCode, confirmPasswordReset, applyActionCode, checkActionCode } from 'firebase/auth';
import SEO from '../components/SEO';

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const company = process.env.REACT_APP_COMPANY_NAME || 'ZimChat';

    // Firebase's Console "Action URL" setting is ONE url shared by every auth
    // email type — it appends ?mode=... to tell us which. This page has to
    // branch on that, or two of the three link types silently land on the
    // wrong screen and never actually complete their action.
    const oobCode = searchParams.get('oobCode');
    const mode = searchParams.get('mode');
    const isConfirmMode      = mode === 'resetPassword' && !!oobCode;
    const isVerifyEmailMode  = mode === 'verifyEmail'   && !!oobCode;
    const isRecoverEmailMode = mode === 'recoverEmail'  && !!oobCode;

    const [email,   setEmail]   = useState('');
    const [sent,    setSent]    = useState(false);
    const [error,   setError]   = useState('');
    const [loading, setLoading] = useState(false);

    // Confirm-mode (password reset) state
    const [verifying, setVerifying] = useState(isConfirmMode);
    const [resetEmail, setResetEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [resetDone, setResetDone] = useState(false);

    // Verify-email mode state
    const [verifyEmailStatus, setVerifyEmailStatus] = useState<'checking' | 'done' | 'error'>('checking');
    const [verifyEmailError, setVerifyEmailError] = useState('');

    // Recover-email mode state (undo an unwanted email change)
    const [recoverStatus, setRecoverStatus] = useState<'checking' | 'done' | 'error'>('checking');
    const [recoverError, setRecoverError] = useState('');
    const [restoredEmail, setRestoredEmail] = useState('');

    useEffect(() => {
        if (!isConfirmMode || !oobCode) return;
        verifyPasswordResetCode(auth, oobCode)
            .then(emailForCode => { setResetEmail(emailForCode); setVerifying(false); })
            .catch(err => {
                setError(
                    err.code === 'auth/expired-action-code' ? 'This reset link has expired. Please request a new one.' :
                    err.code === 'auth/invalid-action-code'  ? 'This reset link is invalid or has already been used.' :
                    'This reset link is no longer valid. Please request a new one.'
                );
                setVerifying(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isConfirmMode, oobCode]);

    useEffect(() => {
        if (!isVerifyEmailMode || !oobCode) return;
        applyActionCode(auth, oobCode)
            .then(() => setVerifyEmailStatus('done'))
            .catch(err => {
                setVerifyEmailError(
                    err.code === 'auth/expired-action-code' ? 'This verification link has expired. Please request a new one from your account settings.' :
                    err.code === 'auth/invalid-action-code'  ? 'This verification link is invalid or has already been used.' :
                    'This verification link is no longer valid.'
                );
                setVerifyEmailStatus('error');
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVerifyEmailMode, oobCode]);

    useEffect(() => {
        if (!isRecoverEmailMode || !oobCode) return;
        checkActionCode(auth, oobCode)
            .then(info => {
                setRestoredEmail(info.data.email || '');
                return applyActionCode(auth, oobCode);
            })
            .then(() => setRecoverStatus('done'))
            .catch(err => {
                setRecoverError(
                    err.code === 'auth/expired-action-code' ? 'This link has expired.' :
                    err.code === 'auth/invalid-action-code'  ? 'This link is invalid or has already been used.' :
                    'This link is no longer valid.'
                );
                setRecoverStatus('error');
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRecoverEmailMode, oobCode]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            // Sent via our own SMTP (server-side), not Firebase Auth's built-in
            // email delivery — see /api/auth/request-password-reset. The
            // response is always the same regardless of whether the email
            // exists, so this can't be used to find out which emails have accounts.
            const apiBase = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');
            const res = await fetch(`${apiBase}/api/auth/request-password-reset`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim() }),
            });
            if (!res.ok) throw new Error('Failed to send reset email. Please try again.');
            setSent(true);
        } catch (err: any) {
            setError(err.message || 'Failed to send reset email. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return; }
        if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
        setLoading(true);
        try {
            await confirmPasswordReset(auth, oobCode!, newPassword);
            setResetDone(true);
            setTimeout(() => navigate('/login'), 2500);
        } catch (err: any) {
            setError(
                err.code === 'auth/expired-action-code' ? 'This reset link has expired. Please request a new one.' :
                err.code === 'auth/weak-password' ? 'Please choose a stronger password.' :
                err.message || 'Failed to reset password. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    const box: React.CSSProperties = {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1E293B 0%, #1E293B 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
    };
    const card: React.CSSProperties = {
        background: 'white', borderRadius: '24px', padding: '48px',
        maxWidth: '480px', width: '100%',
        boxShadow: '0 20px 35px -10px rgba(0,0,0,0.2)',
    };
    const inp: React.CSSProperties = {
        width: '100%', padding: '12px 16px', border: '1px solid #e5e7eb',
        borderRadius: '12px', fontSize: '16px', outline: 'none', boxSizing: 'border-box',
    };
    const btn: React.CSSProperties = {
        width: '100%', background: '#1E293B', color: 'white', padding: '14px',
        borderRadius: '12px', border: 'none', fontSize: '16px', fontWeight: '600',
        cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
        marginBottom: '20px',
    };

    // ── Verify-email mode: landed here from the "confirm your email" link ──
    if (isVerifyEmailMode) {
        return (
            <>
                <SEO title={`Verify Email — ${company}`} description="Confirm your email address" noindex />
                <div style={box}>
                    <div style={card}>
                        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                            <div style={{ fontSize: '48px', marginBottom: '12px' }}>{verifyEmailStatus === 'error' ? '⚠️' : '✉️'}</div>
                            <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Email Verification</h1>
                        </div>
                        {verifyEmailStatus === 'checking' ? (
                            <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>Verifying your email…</div>
                        ) : verifyEmailStatus === 'done' ? (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ background: '#d1fae5', color: '#065f46', padding: '20px', borderRadius: '12px', marginBottom: '24px', fontSize: '15px', lineHeight: 1.6 }}>
                                    ✅ Your email address has been verified!
                                </div>
                                <Link to="/dashboard" style={{ ...btn, display: 'block', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box' }}>Go to Dashboard</Link>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px', borderRadius: '12px', marginBottom: '20px', fontSize: '14px' }}>
                                    {verifyEmailError}
                                </div>
                                <Link to="/login" style={{ color: '#1E293B', fontWeight: 600, fontSize: '14px', textDecoration: 'none' }}>← Back to Sign In</Link>
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    }

    // ── Recover-email mode: landed here from "undo this email change" ──────
    if (isRecoverEmailMode) {
        return (
            <>
                <SEO title={`Restore Email — ${company}`} description="Undo an email address change" noindex />
                <div style={box}>
                    <div style={card}>
                        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                            <div style={{ fontSize: '48px', marginBottom: '12px' }}>{recoverStatus === 'error' ? '⚠️' : '🔁'}</div>
                            <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Restore Email Address</h1>
                        </div>
                        {recoverStatus === 'checking' ? (
                            <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>Restoring your email address…</div>
                        ) : recoverStatus === 'done' ? (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ background: '#d1fae5', color: '#065f46', padding: '20px', borderRadius: '12px', marginBottom: '20px', fontSize: '15px', lineHeight: 1.6 }}>
                                    ✅ Your account email has been restored{restoredEmail ? <> to <strong>{restoredEmail}</strong></> : ''}.
                                </div>
                                <div style={{ background: '#fffbeb', borderLeft: '5px solid #f59e0b', padding: '14px 16px', borderRadius: '10px', marginBottom: '24px', fontSize: '13px', color: '#92400e', textAlign: 'left' }}>
                                    Since your email was changed without your consent, we recommend resetting your password now to secure your account.
                                </div>
                                <Link to="/reset-password" style={{ ...btn, display: 'block', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box' }}>Reset My Password</Link>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px', borderRadius: '12px', marginBottom: '20px', fontSize: '14px' }}>
                                    {recoverError}
                                </div>
                                <Link to="/login" style={{ color: '#1E293B', fontWeight: 600, fontSize: '14px', textDecoration: 'none' }}>← Back to Sign In</Link>
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    }

    // ── Confirm mode: landed here from the email link ──────────────────────
    if (isConfirmMode) {
        return (
            <>
                <SEO title={`Reset Password — ${company}`} description="Choose a new password" noindex />
                <div style={box}>
                    <div style={card}>
                        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔐</div>
                            <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Choose a New Password</h1>
                            {resetEmail && !resetDone && (
                                <p style={{ color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>for <strong>{resetEmail}</strong></p>
                            )}
                        </div>

                        {verifying ? (
                            <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>Checking your reset link…</div>
                        ) : resetDone ? (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ background: '#d1fae5', color: '#065f46', padding: '20px', borderRadius: '12px', marginBottom: '24px', fontSize: '15px', lineHeight: 1.6 }}>
                                    ✅ Password updated! Taking you to sign in…
                                </div>
                            </div>
                        ) : error && !resetEmail ? (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px', borderRadius: '12px', marginBottom: '20px', fontSize: '14px' }}>
                                    {error}
                                </div>
                                <Link to="/reset-password" style={{ color: '#1E293B', fontWeight: 600, fontSize: '14px', textDecoration: 'none' }}>Request a new link →</Link>
                            </div>
                        ) : (
                            <form onSubmit={handleConfirm}>
                                {error && (
                                    <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px', borderRadius: '12px', marginBottom: '20px', fontSize: '14px', textAlign: 'center' }}>
                                        {error}
                                    </div>
                                )}
                                <div style={{ marginBottom: '18px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>New Password</label>
                                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={6} placeholder="At least 6 characters" style={inp} />
                                </div>
                                <div style={{ marginBottom: '24px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>Confirm Password</label>
                                    <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required minLength={6} placeholder="Re-enter your new password" style={inp} />
                                </div>
                                <button type="submit" disabled={loading} style={btn}>
                                    {loading ? 'Updating…' : 'Update Password'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </>
        );
    }

    // ── Request mode: enter your email to get a reset link ─────────────────
    return (
        <>
            <SEO title={`Reset Password — ${company}`} description="Reset your account password" />
            <div style={box}>
                <div style={card}>
                    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                        <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔐</div>
                        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Reset Password</h1>
                        <p style={{ color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>We'll email you a link to reset your password</p>
                    </div>

                    {sent ? (
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ background: '#d1fae5', color: '#065f46', padding: '20px', borderRadius: '12px', marginBottom: '24px', fontSize: '15px', lineHeight: 1.6 }}>
                                ✅ Reset link sent to <strong>{email}</strong>.<br />
                                Check your inbox — and spam folder just in case.
                            </div>
                            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
                                Didn't receive it?{' '}
                                <button onClick={() => setSent(false)} style={{ background: 'none', border: 'none', color: '#1E293B', fontWeight: '600', cursor: 'pointer', fontSize: '14px', padding: 0 }}>
                                    Try again
                                </button>
                            </p>
                            <Link to="/login" style={{ color: '#6b7280', fontSize: '14px', textDecoration: 'none' }}>← Back to Sign In</Link>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px', borderRadius: '12px', marginBottom: '20px', fontSize: '14px', textAlign: 'center' }}>
                                    {error}
                                </div>
                            )}
                            <form onSubmit={handleSubmit}>
                                <div style={{ marginBottom: '24px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        required
                                        placeholder="you@example.com"
                                        style={inp}
                                        onFocus={e => (e.target.style.borderColor = '#1E293B')}
                                        onBlur={e  => (e.target.style.borderColor = '#e5e7eb')}
                                    />
                                </div>
                                <button type="submit" disabled={loading} style={btn}>
                                    {loading ? 'Sending…' : 'Send Reset Link'}
                                </button>
                            </form>
                            <div style={{ textAlign: 'center' }}>
                                <Link to="/login" style={{ color: '#6b7280', fontSize: '14px', textDecoration: 'none' }}>
                                    ← Back to Sign In
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}
